import React from "react";
import { PenLine, Plus } from "lucide-react";
import { Button } from "../ui";
import { registeredCtaCopy } from "../../lib/classGuestList";

const QUIET_STATUS =
  "w-full sm:w-auto bg-canvas border border-black/[0.06] rounded-btn px-5 py-3 inline-flex flex-col items-start gap-0.5 shadow-none hover:bg-black/[0.03] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orchid/45 text-left transition-colors";

/**
 * Primary CTA states for a class instance — sits on the quiet light hero body.
 */
export default function ClassHeroCta({
  isCancelled,
  myRsvp,
  pendingPayment,
  signedIn,
  isAdmin,
  needsRegistrationSetup = false,
  entitlementPreview,
  rsvpBusy = false,
  rsvpLoading = false,
  onOpenForm,
  onRsvp,
  onAddStudent,
  onSetupRegistration,
}) {
  const showLoader = rsvpBusy || rsvpLoading;
  if (isCancelled) {
    return (
      <p className="font-ui font-medium text-[14px] text-ink/60">
        This session is cancelled.
      </p>
    );
  }

  if (isAdmin) {
    return (
      <div className="flex flex-wrap gap-3 items-stretch">
        {needsRegistrationSetup ? (
          <Button
            variant="primary"
            size="lg"
            onClick={onSetupRegistration}
            className="inline-flex items-center justify-center"
            data-testid="open-class-registration-setup-btn"
          >
            Add registration option
          </Button>
        ) : null}
        <Button
          variant={needsRegistrationSetup ? "secondary" : "primary"}
          size="lg"
          onClick={onAddStudent}
          className="inline-flex items-center justify-center gap-1.5"
          data-testid="open-host-add-student-btn"
          aria-label="Add student"
        >
          <Plus size={18} strokeWidth={2} aria-hidden />
          Student
        </Button>
      </div>
    );
  }

  if (myRsvp && myRsvp.status !== "CANCELLED") {
    const copy = registeredCtaCopy(myRsvp, entitlementPreview) || {
      title: "You're in!",
      detail: null,
    };
    return (
      <div className="flex flex-wrap gap-3 items-stretch">
        <button
          type="button"
          onClick={onOpenForm}
          aria-label="View RSVP"
          className={QUIET_STATUS}
          data-testid="class-rsvp-confirmed-badge"
        >
          <span className="inline-flex items-center gap-2">
            <PenLine className="w-4 h-4 text-ink/50 shrink-0" strokeWidth={2} aria-hidden />
            <span className="font-ui font-medium text-[15px] text-ink">{copy.title}</span>
          </span>
          {copy.detail ? (
            <span className="font-ui text-[12px] font-medium text-ink/55 pl-6" data-testid="class-rsvp-credit-pending">
              {copy.detail}
            </span>
          ) : null}
        </button>
      </div>
    );
  }

  if (pendingPayment) {
    return (
      <div className="flex flex-wrap gap-3 items-stretch">
        <div className={QUIET_STATUS} data-testid="class-payment-pending-badge">
          <span className="font-ui font-medium text-[15px] text-ink">
            Waiting for the school to approve your payment
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-3 items-stretch">
      <Button
        variant="primary"
        size="lg"
        onClick={onRsvp || onOpenForm}
        loading={showLoader}
        loadingLabel="Loading RSVP options"
        className="min-w-[7.5rem]"
        data-testid={signedIn ? "open-class-rsvp-btn" : "class-sign-in-rsvp-btn"}
      >
        RSVP →
      </Button>
    </div>
  );
}

export function getClassStickyCtaState({
  isCancelled,
  myRsvp,
  pendingPayment,
  signedIn,
  isAdmin,
  needsRegistrationSetup = false,
}) {
  if (isCancelled) return null;
  if (isAdmin) {
    if (needsRegistrationSetup) {
      return {
        label: "Add registration option",
        disabled: false,
        testId: "sticky-class-registration-setup-btn",
        action: "setup",
      };
    }
    return { label: "+ Student", disabled: false, testId: "sticky-host-add-student-btn", action: "add" };
  }
  if (myRsvp && myRsvp.status !== "CANCELLED") return null;
  if (pendingPayment) return null;
  // Unauth and signed-in both use RSVP → (auth/gate handled by click handler)
  return {
    label: "RSVP →",
    disabled: false,
    testId: signedIn ? "sticky-open-class-rsvp-btn" : "sticky-class-sign-in-btn",
    action: "rsvp",
  };
}
